import { useTranslation } from 'react-i18next';
import { Check, Sparkles, UserCheck } from 'lucide-react';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';

interface Props {
  identification: VoiceIdentification;
  /** Directory name for an id, or undefined when it is no longer in the list. */
  resolveSpeakerName: (speakerId: number) => string | undefined;
  /** The speaker name currently shown on the row. */
  currentName: string;
  /** Accept: records the decision and applies the name to the document. */
  onUseSuggestion: (speakerId: number, name: string) => void;
  /** Open the full picker to choose a different person. */
  onChoosePerson: () => void;
  disabled?: boolean;
}

/** A reviewable suggestion next to the speaker label.
 *
 * Nothing is applied on its own: the chip is an offer the editor accepts, so
 * an existing correction is never silently overwritten. Once a human decision
 * exists it shows as confirmed instead of nagging again.
 *
 * `override_set` — not `reason` — decides whether a decision has been made;
 * `reason` only describes what the model produced. */
export const VoiceSuggestionChip = ({
  identification,
  resolveSpeakerName,
  currentName,
  onUseSuggestion,
  onChoosePerson,
  disabled = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const decided = identification.override_set;
  const suggestedId = identification.suggested_speaker_id;
  const suggestedName =
    suggestedId != null ? resolveSpeakerName(suggestedId) : undefined;

  // A decision has been recorded: confirm it quietly and stop suggesting.
  if (decided) {
    const chosenName =
      identification.override_speaker_id != null
        ? resolveSpeakerName(identification.override_speaker_id)
        : undefined;
    const labelText =
      identification.override_speaker_id === null
        ? t('voice-marked-unknown')
        : t('voice-assigned-to', { name: chosenName ?? t('voice-unknown') });
    return (
      <Tooltip label={t('voice-decision-recorded-hint')}>
        <button
          type="button"
          onClick={onChoosePerson}
          disabled={disabled}
          aria-label={labelText}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          // The chip sits inline in the editor, not inside a Modal, so it
          // cannot rely on inheriting a text colour: without this it renders
          // black on the dark surface in dark mode.
          style={{
            backgroundColor: getColour('secondLayer'),
            color: getColour('normalText')
          }}
        >
          <UserCheck size={12} aria-hidden />
          <span className="max-w-32 truncate">{labelText}</span>
        </button>
      </Tooltip>
    );
  }

  // No suggestion worth reviewing: unknown and ambiguous voices stay usable
  // without being pushed toward a match.
  if (!suggestedName || suggestedId == null) return null;

  // The row already shows this person — reviewing adds nothing, but the
  // decision is still unrecorded, so offer confirmation rather than a rename.
  const alreadyNamed =
    currentName.trim().toLowerCase() === suggestedName.trim().toLowerCase();

  return (
    <Tooltip
      label={
        alreadyNamed
          ? t('voice-confirm-identity-hint')
          : t('voice-use-suggestion-hint', { name: suggestedName })
      }
    >
      <button
        type="button"
        onClick={() => onUseSuggestion(suggestedId, suggestedName)}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          backgroundColor: getColour('secondLayer'),
          color: getColour('normalText')
        }}
      >
        {alreadyNamed ? (
          <Check size={12} aria-hidden />
        ) : (
          <Sparkles size={12} aria-hidden />
        )}
        <span className="max-w-32 truncate">
          {alreadyNamed
            ? t('voice-confirm-identity')
            : t('voice-use-suggestion-named', { name: suggestedName })}
        </span>
      </button>
    </Tooltip>
  );
};
